import React, {useMemo} from 'react';
import {classes, style} from './doppe-action-type-gallery-list-view-item.st.css';
import {cssEllipsis, flexColumn, RatioMaintainerIgnoreClassName, reactCreateMemo} from '@wix/devzai-utils-react';
import {evaluateFunction} from '@wix/devzai-utils-common';
import {
    DoppeActionLayout,
    doppeHideableValueIsVisibleAndNotEqualValue,
    DoppeListActionSettings
} from '../../../../client-server-common';
import {WidgetRenderingContextType} from '../../../../client-server-common/doppe-action-types/doppe-action-type';
import {IDoppeViewerActionContext} from '../../../../doppe-sdk';
import {
    useAssertWidgetRenderingContextType
} from '../../../../components/doppe-viewer-widget-rendering-context/doppe-viewer-widget-rendering-context';
import {
    DoppeActionTypeBaseListViewItem, DoppeActionTypeBaseListViewItemLayout
} from '../doppe-action-type-base-list-view-item/doppe-action-type-base-list-view-item';
import {CommonClasses, FlexLayout} from '@wix/devzai-common-client';
import {DoppeActionTypeMediaGallery} from '../../../doppe-action-type-media-gallery/doppe-action-type-media-gallery';
import {DoppePageMediaView} from '../../../../components/doppe-page-media-view/doppe-page-media-view';
import {
    DoppeActionTypeCommonListViewItem
} from '../doppe-action-type-common-list-view-item/doppe-action-type-common-list-view-item';

const WidgetRenderingContextTypeCssState = {
    InPage: 'inPage',
    Modal: 'modal',
    Strip: 'strip',
}

export const DoppeActionTypeGalleryListViewItem = reactCreateMemo(function DoppeActionTypeGalleryListViewItem<PROPS extends DoppeListActionSettings.WithItemClickBehaviour>(
    props: {
        listItemSettings: DoppeActionTypeMediaGallery.MediaGalleryListItemSettings;
        layout: DoppeActionLayout;
        isOneItem: boolean;
        hideTitleWhenPossible?: boolean;
        hideDescriptionWhenPossible?: boolean;
        actionContext?: IDoppeViewerActionContext<PROPS>;
        innerItemIndex: number;
        innerItemId: string;
    }
) {
    const {
        listItemSettings,
        layout,
        isOneItem = false,
        hideTitleWhenPossible = false,
        hideDescriptionWhenPossible = false,
        actionContext,
        innerItemIndex,
        innerItemId,
    } = props;

    const {
        ribbon,
        description,
        title,
        media,
    } = listItemSettings;


    const widgetRenderingContextType = useAssertWidgetRenderingContextType();

    const mediaViewLayoutSpec = useMemo(() => {
        switch (layout) {
            case DoppeActionLayout.Column: {
                return {widthHeightRatio: 1};
            }
            case DoppeActionLayout.Grid: {
                return {widthHeightRatio:  1.2};
            }
            case DoppeActionLayout.ImagesGrid: {
                return {widthHeightRatio: 1};
            }
            case DoppeActionLayout.LargeColumn: {
                return {widthHeightRatio:  1.5};
            }
            case DoppeActionLayout.Swiper: {
                return {widthHeightRatio:  1.2};
            }
            case DoppeActionLayout.Carousel: {
                return {widthHeightRatio:  1.2};
            }
            case DoppeActionLayout.CarouselSmall: {
                return {widthHeightRatio:  1.2};
            }
        }
    }, [layout]);

    const mediaViewWidthSpec = useMemo(() => {
        switch (layout) {
            case DoppeActionLayout.Column: {
                return {width: 60}
            }
            case DoppeActionLayout.Grid: {
                return {contentRelativeWidth: 0.37}
            }
            case DoppeActionLayout.ImagesGrid: {
                return {contentRelativeWidth: 0.3}
            }
            case DoppeActionLayout.LargeColumn: {
                return {contentRelativeWidth: 0.8}
            }
            case DoppeActionLayout.Swiper: {
                return {contentRelativeWidth: 0.8}
            }
            case DoppeActionLayout.Carousel: {
                return {contentRelativeWidth: 0.7}
            }
            case DoppeActionLayout.CarouselSmall: {
                return {contentRelativeWidth: 0.35}
            }
        }
    }, [layout])

    switch (layout) {
        case DoppeActionLayout.Column:
        case DoppeActionLayout.ImagesGrid:
        case DoppeActionLayout.Grid:
        case DoppeActionLayout.CarouselSmall:
        case DoppeActionLayout.LargeColumn:
        case DoppeActionLayout.Swiper:
        case DoppeActionLayout.Carousel: {
            return <DoppeActionTypeCommonListViewItem
                actionContext={actionContext}
                innerItemId={innerItemId}
                innerItemIndex={innerItemIndex}
                title={title}
                description={doppeHideableValueIsVisibleAndNotEqualValue(description, '') ? description : ''}
                image={media}
                layout={layout}
                ribbon={doppeHideableValueIsVisibleAndNotEqualValue(ribbon, '') ? ribbon : undefined}
                isOneItem={isOneItem}
                hideImageWhenPossible={false}
                hideTitleWhenPossible={hideTitleWhenPossible}
                hideDescriptionWhenPossible={hideDescriptionWhenPossible}
            />;
        }
        default: {
            return (
                <DoppeActionTypeBaseListViewItem
                    itemLayout={DoppeActionTypeBaseListViewItemLayout.Button}
                    className={style(classes.root, {
                        layout: layout,
                        isOneItem: isOneItem,
                        hasImage: !!media,
                        widgetRenderingContextType: evaluateFunction(() => {
                            switch (widgetRenderingContextType) {
                                case WidgetRenderingContextType.Modal: {
                                    return WidgetRenderingContextTypeCssState.Modal;
                                }
                                case WidgetRenderingContextType.Card: {
                                    return WidgetRenderingContextTypeCssState.InPage;
                                }
                                case WidgetRenderingContextType.Strip: {
                                    return WidgetRenderingContextTypeCssState.InPage;
                                }
                            }
                        }),
                    }, flexColumn())}
                    actionContext={actionContext}
                    innerItemIndex={innerItemIndex}
                    innerItemId={innerItemId}
                    contentClassName={style(classes.actionTypeListViewItemViewRootContent, flexColumn())}
                >
                    <div
                        className={style(classes.linkImageContainer)}
                    >
                        <DoppePageMediaView
                            mediaResource={media}
                            layoutSpec={mediaViewLayoutSpec}
                            widthSpec={mediaViewWidthSpec}
                        />
                        {
                            title && !hideTitleWhenPossible ?
                                <div
                                    className={style(classes.textOnImage, {}, RatioMaintainerIgnoreClassName, cssEllipsis(), FlexLayout.row.alignToBottom)}
                                >
                                    <div
                                        className={style(classes.textOnImageText, {}, cssEllipsis(), FlexLayout.fillRemainingHorizontalSpace)}
                                    >
                                        {title}
                                    </div>

                                </div>
                                :
                                null
                        }
                        {
                            ribbon ?
                                <div
                                    className={style(classes.ribbonContainer, {dockedToTop: layout === DoppeActionLayout.ImagesGrid}, RatioMaintainerIgnoreClassName, FlexLayout.column.alignToStart)}
                                >
                                    <div
                                        className={style(classes.ribbon, {}, CommonClasses.ellipsis)}
                                    >
                                        {ribbon}
                                    </div>
                                </div>
                                :
                                null
                        }
                    </div>
                </DoppeActionTypeBaseListViewItem>
            );
        }
    }
});



